// =============================================================================
// Permission Configuration - Single Source of Truth
// =============================================================================

// Role types (mapping: customer_g1 -> customer for display, see ROLE_MAP)
export type Role = 'admin' | 'analyst' | 'viewer' | 'customer'

// Backend role names (for compatibility with existing User model)
export type BackendRole = 'admin' | 'analyst' | 'viewer' | 'customer_g1'

// Map backend role names to frontend role names
export const ROLE_MAP: Record<BackendRole, Role> = {
  admin: 'admin',
  analyst: 'analyst',
  viewer: 'viewer',
  customer_g1: 'customer',
}

// Resource types
export type Resource =
  | 'dashboard'
  | 'portfolio'
  | 'analytics'
  | 'layers'
  | 'reports'
  | 'settings'
  | 'users'
  | 'docs'

// Action types (:own suffix handled in logic)
export type Action = 'view' | 'create' | 'edit' | 'delete' | 'export' | 'manage'
export type ActionWithOwn = Action | `${Action}:own`

// =============================================================================
// Permission Matrix
// =============================================================================

export const PERMISSION_MATRIX: Record<Resource, Record<Role, ActionWithOwn[]>> = {
  dashboard: {
    admin: ['view', 'edit'],
    analyst: ['view', 'edit'],
    viewer: ['view'],
    customer: ['view'],
  },
  portfolio: {
    admin: ['view', 'create', 'edit', 'delete'],
    analyst: ['view', 'create', 'edit', 'delete'],
    viewer: ['view'], // Read-only access (validated decision)
    customer: ['view:own', 'create', 'edit:own', 'delete:own'],
  },
  analytics: {
    admin: ['view', 'export'],
    analyst: ['view', 'export'],
    viewer: [],      // No access - admin/analyst only
    customer: [],    // No access - admin/analyst only
  },
  layers: {
    admin: ['view', 'manage'],
    analyst: ['view', 'manage'],
    viewer: [],
    customer: [],
  },
  reports: {
    admin: ['view', 'create', 'delete'],
    analyst: ['view', 'create', 'delete'],
    viewer: ['view:own'], // Read-only: can view reports shared with them
    customer: ['view:own', 'create', 'delete:own'],
  },
  settings: {
    admin: ['view', 'manage'],
    analyst: ['view'],
    viewer: ['view'],
    customer: ['view'],
  },
  users: {
    admin: ['view', 'create', 'edit', 'delete'],
    analyst: [],
    viewer: [],
    customer: [],
  },
  docs: {
    admin: ['view'],
    analyst: ['view'],
    viewer: ['view'],
    customer: ['view'],
  },
}

// =============================================================================
// Navigation Configuration
// =============================================================================

export interface NavItem {
  path: string
  label: string
  resource: Resource
  action: Action
  icon?: string
  children?: NavItem[]
}

export const NAV_ITEMS: NavItem[] = [
  { path: '/dashboard', label: 'Dashboard', resource: 'dashboard', action: 'view' },
  { path: '/portfolio', label: 'Portfolio', resource: 'portfolio', action: 'view' },
  { path: '/analytics', label: 'Analytics', resource: 'analytics', action: 'view' },
  { path: '/layers', label: 'Layers', resource: 'layers', action: 'view' },
  { path: '/reports', label: 'Reports', resource: 'reports', action: 'view' },
  { path: '/docs', label: 'Documentation', resource: 'docs', action: 'view' },
  {
    path: '/settings',
    label: 'Settings',
    resource: 'settings',
    action: 'view',
    children: [
      { path: '/settings/users', label: 'Users', resource: 'users', action: 'view' },
    ],
  },
]

// =============================================================================
// Default Routes per Role
// =============================================================================

export const DEFAULT_ROUTES: Record<Role, string> = {
  admin: '/dashboard',
  analyst: '/dashboard',
  viewer: '/portfolio',   // Changed from /analytics (no longer accessible)
  customer: '/portfolio',
}

// =============================================================================
// Permission Check Helper
// =============================================================================

/**
 * Check if role has permission for resource+action
 * Handles :own suffix logic
 */
export function hasPermission(
  role: Role,
  resource: Resource,
  action: Action,
  isOwner?: boolean
): boolean {
  const permissions = PERMISSION_MATRIX[resource]?.[role] || []

  // Check exact match
  if (permissions.includes(action)) return true

  // Check :own suffix (requires ownership)
  if (permissions.includes(`${action}:own` as ActionWithOwn)) {
    return isOwner === true
  }

  return false
}

/**
 * Check if role has ANY permission for resource+action (including :own variant)
 * Used for navigation visibility - shows menu if user has any access
 */
export function hasAnyPermission(
  role: Role,
  resource: Resource,
  action: Action
): boolean {
  const permissions = PERMISSION_MATRIX[resource]?.[role] || []

  // Check exact match OR :own variant
  return permissions.includes(action) || permissions.includes(`${action}:own` as ActionWithOwn)
}

/**
 * Normalize backend role to frontend role
 */
export function normalizeRole(backendRole: BackendRole | string): Role {
  return ROLE_MAP[backendRole as BackendRole] || 'viewer'
}

/**
 * Get all permissions for a role
 */
export function getRolePermissions(role: Role): Map<Resource, ActionWithOwn[]> {
  const permissions = new Map<Resource, ActionWithOwn[]>()
  for (const [resource, rolePerms] of Object.entries(PERMISSION_MATRIX)) {
    permissions.set(resource as Resource, rolePerms[role] || [])
  }
  return permissions
}

/**
 * Check if role can access a nav item
 * Uses hasAnyPermission to include :own permissions for nav visibility
 */
export function canAccessNavItem(role: Role, navItem: NavItem): boolean {
  return hasAnyPermission(role, navItem.resource, navItem.action)
}

/**
 * Filter nav items by role permissions
 */
export function filterNavItemsByRole(role: Role, items: NavItem[] = NAV_ITEMS): NavItem[] {
  return items
    .filter((item) => canAccessNavItem(role, item))
    .map((item) => ({
      ...item,
      children: item.children ? filterNavItemsByRole(role, item.children) : undefined,
    }))
}
